import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Star, X, MessageCircle, Image } from "lucide-react";
import api, { photoUrl, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { 
  Heart, Eye, Users, Sparkles, ArrowRight, Crown, 
  Bell, Check, Share2, Award, QrCode, Trophy, Shield, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [picks, setPicks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [blockLeaderboard, setBlockLeaderboard] = useState([]);
  const [publicSettings, setPublicSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [refreshingRecs, setRefreshingRecs] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, picksRes, notifsRes, refRes, settingsRes, blockLeaderboardRes, recsRes, viewedRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/browse", { params: { limit: 6 } }),
        api.get("/notifications"),
        api.get("/referrals/stats").catch(() => null), // fail gracefully if not configured
        api.get("/settings/public"),
        api.get("/referrals/block-leaderboard").catch(() => null),
        api.get("/recommendations").catch(() => ({ data: [] })),
        api.get("/me/viewed").catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setPicks(picksRes.data.results || []);
      setNotifications(notifsRes.data || []);
      if (refRes) setReferralStats(refRes.data);
      if (blockLeaderboardRes) setBlockLeaderboard(blockLeaderboardRes.data || []);
      setPublicSettings(settingsRes.data);
      setRecommendations(recsRes.data || []);
      setRecentlyViewed(viewedRes.data || []);
    } catch (err) {
      console.error("Error loading dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshRecommendations = async () => {
    setRefreshingRecs(true);
    try {
      const res = await api.get("/recommendations", { params: { refresh_cache: true } });
      setRecommendations(res.data || []);
      toast.success(language === "hi" ? "एआई अनुशंसाएं सफलतापूर्वक अपडेट की गईं!" : "AI recommendations updated successfully!");
    } catch {
      toast.error(language === "hi" ? "अनुशंसाएं अपडेट करने में विफल" : "Failed to update recommendations");
    } finally {
      setRefreshingRecs(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (stats && stats.profile_completion < 80) {
      const dismissed = sessionStorage.getItem("dismissed_profile_alert");
      if (!dismissed) {
        setShowCompleteModal(true);
      }
    }
  }, [stats]);

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await api.post("/profile/send-completion-email");
      toast.success(language === "hi" ? "चेकलिस्ट ईमेल सफलतापूर्वक भेजा गया! कृपया अपना इनबॉक्स देखें।" : "Checklist email sent successfully! Please check your inbox.");
      setShowCompleteModal(false);
      sessionStorage.setItem("dismissed_profile_alert", "true");
    } catch (err) {
      toast.error(language === "hi" ? "चेकलिस्ट ईमेल भेजने में विफल। कृपया पुनः प्रयास करें।" : "Failed to send checklist email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDismiss = () => {
    setShowCompleteModal(false);
    sessionStorage.setItem("dismissed_profile_alert", "true");
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success(language === "hi" ? "सभी सूचनाएं पढ़ी गईं चिह्नित की गईं" : "All notifications marked as read");
    } catch {
      toast.error(language === "hi" ? "सूचनाओं को पढ़ा गया चिह्नित करने में विफल" : "Failed to mark notifications as read");
    }
  };

  const handleShareReferral = () => {
    if (!referralStats) return;
    const refLink = `${window.location.origin}/register?ref=${referralStats.referral_code}`;
    const defaultTemplate = language === "hi"
      ? "हमरसाथी पर अपना आदर्श जीवनसाथी खोजें! मेरे लिंक का उपयोग करके पंजीकरण करें: {link}"
      : "Find your perfect life partner on Hamarsathi! Register using my link: {link}";
    const rawTemplate = publicSettings?.whatsapp_template || defaultTemplate;
    const message = rawTemplate.replace("{link}", refLink);
    
    // Copy link to clipboard
    navigator.clipboard.writeText(refLink);
    toast.success(language === "hi" ? "रेफरल लिंक क्लिपबोर्ड पर कॉपी किया गया!" : "Referral link copied to clipboard!");

    // Open WhatsApp
    const encoded = encodeURIComponent(message);
    window.open(`https://api.whatsapp.com/send?text=${encoded}`, "_blank");
  };

  if (loading) {
    return <div className="container-px py-16 text-muted-foreground text-center">{language === "hi" ? "डैशबोर्ड लोड हो रहा है..." : "Loading dashboard..."}</div>;
  }

  const referralLink = referralStats ? `${window.location.origin}/register?ref=${referralStats.referral_code}` : "";
  const qrCodeUrl = referralLink 
    ? `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(referralLink)}` 
    : "";

  return (
    <div className="container-px py-8 lg:py-10 max-w-7xl mx-auto">
      
      {/* Upper Layout Greeting & Health */}
      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
        
        {/* Greeting Card */}
        <div className="lg:col-span-2 card-soft p-6 sm:p-8 relative overflow-hidden flex flex-col justify-between">
          <div className="absolute inset-0 mandala-bg pointer-events-none opacity-40" />
          <div className="relative z-10">
            <span className="label-cap">{t("dashboard.welcome")}</span>
            <h1 className="font-heading text-4xl sm:text-5xl text-primary mt-2">
              {language === "hi" ? `नमस्ते, ${user?.name?.split(" ")[0] || "दोस्त"}` : `Namaste, ${user?.name?.split(" ")[0] || "friend"}`}.
            </h1>
            <p className="text-muted-foreground mt-3 max-w-xl">
              {stats?.profile_completion < 80
                ? (language === "hi" ? "अधिक मैच और कनेक्शन अनुरोध प्राप्त करने के लिए अपनी प्रोफ़ाइल पूरी करें।" : "Complete your profile to get more matches and connection requests.")
                : (language === "hi" ? "आपकी प्रोफ़ाइल शानदार लग रही है! नीचे आज के चुने हुए मैचों को देखें।" : "Your profile is shining! Explore today's curated matches below.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="dash-browse">
                <Link to="/app/browse">
                  {language === "hi" ? "मैच ब्राउज़ करें" : "Browse Matches"} <ArrowRight className="w-4 h-4 ml-1.5"/>
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full" data-testid="dash-edit">
                <Link to="/app/profile">{language === "hi" ? "प्रोफ़ाइल संपादित करें" : "Edit Profile"}</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full" data-testid="dash-kundli">
                <Link to="/app/kundli"><Sparkles className="w-4 h-4 mr-1.5 text-secondary"/>{t("nav.kundli")}</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full gap-1.5" data-testid="dash-biodata">
                <Link to="/app/biodata"><FileText className="w-4 h-4 text-secondary"/>Bio-Data</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Health Widget */}
        <div className="card-soft p-6 flex flex-col justify-between">
          <div>
            <span className="label-cap">{t("dashboard.completion")}</span>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-5xl text-primary">{stats?.profile_completion ?? 0}%</span>
              <span className="text-muted-foreground text-sm font-medium">
                {language === "hi" ? "पूर्ण" : "complete"}
              </span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-secondary transition-all" style={{ width: `${stats?.profile_completion ?? 0}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              {language === "hi" ? "85% से अधिक पूर्ण प्रोफ़ाइल को 5 गुना अधिक रुचियां प्राप्त होती हैं।" : "Profiles over 85% complete receive up to 5× more interests."}
            </p>
          </div>
          {!user?.is_premium && (
            <Button asChild size="sm" className="mt-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="dash-go-premium">
              <Link to="/app/premium">
                <Crown className="w-4 h-4 mr-1.5 text-secondary"/>
                {language === "hi" ? "प्रीमियम में अपग्रेड करें" : "Upgrade to Premium"}
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Stat Tiles (Views, Interests Recv, Interests Sent, Mutual Matches) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat tileId="stat-views" icon={Eye} label={t("dashboard.metrics.views")} value={stats?.profile_views ?? 0} to="/app/viewers" />
        <Stat tileId="stat-recv" icon={Heart} label={t("dashboard.metrics.recInterests")} value={stats?.interests_received ?? 0} to="/app/interests" />
        <Stat tileId="stat-sent" icon={Heart} label={t("dashboard.metrics.sentInterests")} value={stats?.interests_sent ?? 0} to="/app/interests" />
        <Stat tileId="stat-mutual" icon={Users} label={t("dashboard.metrics.matches")} value={stats?.interests_mutual ?? 0} to="/app/interests" />
      </div>

      {/* Bottom Layout - Matches, Referrals & Notifications */}
      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mt-10">
        
        {/* Left Side: Curated matches + Referrals */}
        <div className="lg:col-span-2 space-y-8">

          {/* Daily Recommendations */}
          {picks.length > 0 && (
            <div className="mb-6">
              <div className="mb-3">
                <span className="label-cap">{language === "hi" ? "दैनिक अनुशंसाएं" : "Daily Recommendations"}</span>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {picks.map((p) => (
                  <Link 
                    key={p.user_id} 
                    to={`/app/profile/${p.user_id}`} 
                    className="flex-shrink-0 flex flex-col items-center gap-1.5 w-20 snap-start group"
                  >
                    <div className="relative w-16 h-16 rounded-full border-2 border-primary/30 p-0.5 bg-surface-container-lowest shadow-md group-hover:border-primary transition-all duration-300">
                      <img 
                        src={p.photos && p.photos.length > 0 ? photoUrl(p.photos[0]) : "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=150"} 
                        alt="" 
                        className="w-full h-full rounded-full object-cover" 
                      />
                      {p.is_verified && (
                        <div className="absolute bottom-0 right-0 bg-yellow-400 p-0.5 rounded-full border border-white shadow-sm flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-center truncate w-full text-foreground/90 group-hover:text-primary transition-colors">
                      {p.name.split(" ")[0]}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          {/* Curated Matches */}
          <div>
            <div className="flex items-end justify-between mb-5">
              <div>
                <span className="label-cap">{language === "hi" ? "आपके लिए" : "For you"}</span>
                <h2 className="font-heading text-3xl text-primary mt-1">
                  {language === "hi" ? "आज के मैच" : "Today's matches"}
                </h2>
              </div>
              <Link to="/app/browse" className="text-sm text-primary font-semibold hover:underline" data-testid="dash-see-all">
                {language === "hi" ? "सभी देखें" : "See all"}
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {picks.map((p) => <ProfileTile key={p.user_id} p={p} />)}
              {picks.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground border border-dashed border-border rounded-2xl">
                  {language === "hi" ? "अभी तक आपकी प्रोफ़ाइल से मेल खाने वाला कोई सुझाव नहीं है।" : "No suggestions matching your profile yet."}{" "}
                  <Link to="/app/profile" className="text-primary underline">
                    {t("dashboard.completeProfile")}
                  </Link>{" "}
                  {language === "hi" ? "ताकि सुझाव मिल सकें।" : "to get suggestions."}
                </div>
              )}
            </div>
          </div>

          {/* AI Matches Section */}
          <div>
            <style>{`
              .no-scrollbar::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            <div className="flex items-end justify-between mb-5">
              <div>
                <span className="label-cap">{language === "hi" ? "आपके लिए एआई" : "AI Recommendations"}</span>
                <h2 className="font-heading text-3xl text-primary mt-1 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-emerald-500 animate-pulse" />
                  {language === "hi" ? "अनुकूलित जीवनसाथी" : "AI Matches for You"}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleRefreshRecommendations}
                disabled={refreshingRecs}
                className="text-xs font-semibold text-muted-foreground hover:text-primary transition flex items-center gap-1 bg-muted px-3 py-1.5 rounded-full"
              >
                🔄 {refreshingRecs ? (language === "hi" ? "अपडेट हो रहा है..." : "Updating...") : (language === "hi" ? "अपडेट करें" : "Refresh AI")}
              </button>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {recommendations.map((rec) => (
                <AIRecommendedProfileTile 
                  key={rec.user.user_id} 
                  rec={rec} 
                  onShowBreakdown={setSelectedMatch} 
                />
              ))}
              {recommendations.length === 0 && (
                <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-2xl bg-white/50 backdrop-blur-md w-full">
                  {language === "hi" ? "अभी तक कोई एआई मेल नहीं मिला। कृपया अपनी प्रोफ़ाइल पूरी करें।" : "No AI recommendations computed yet. Complete your profile details first."}
                </div>
              )}
            </div>
          </div>

          {/* Recently Viewed Profiles */}
          {recentlyViewed.length > 0 && (
            <div>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <span className="label-cap">{language === "hi" ? "हाल ही में देखे गए" : "Recently Viewed"}</span>
                  <h2 className="font-heading text-3xl text-primary mt-1">
                    {language === "hi" ? "हाल के प्रोफाइल" : "Recently Viewed Profiles"}
                  </h2>
                </div>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {recentlyViewed.map((viewedUser) => (
                  <div key={viewedUser.user_id} className="snap-start shrink-0 w-64 bg-white dark:bg-zinc-900 border border-border p-4 rounded-2xl flex flex-col justify-between hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                      <Link to={`/app/profile/${viewedUser.user_id}`} className="shrink-0">
                        <img 
                          src={photoUrl(viewedUser.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=150"} 
                          alt="" 
                          className="w-14 h-14 rounded-xl object-cover border border-border" 
                        />
                      </Link>
                      <div className="min-w-0 flex-1">
                        <Link to={`/app/profile/${viewedUser.user_id}`} className="font-semibold text-sm hover:underline text-primary truncate block">
                          {viewedUser.name}
                        </Link>
                        <p className="text-xs text-muted-foreground mt-0.5">{viewedUser.age} yrs · {viewedUser.district}</p>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{viewedUser.occupation}</p>
                      </div>
                    </div>
                    <div className="mt-3.5 pt-3 border-t border-border/60 flex justify-between items-center text-[11px] text-muted-foreground">
                      <span>{viewedUser.religion} · {viewedUser.caste || "—"}</span>
                      <Link to={`/app/profile/${viewedUser.user_id}`} className="text-secondary font-semibold hover:underline">
                        {language === "hi" ? "देखें" : "View"} →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Referral Center */}
          {publicSettings?.referrals_enabled && referralStats && (
            <div className="card-soft p-6">
              <span className="label-cap">{language === "hi" ? "आमंत्रित करें और बोनस कमाएं" : "Invite & Earn Referral Bonus"}</span>
              <h2 className="font-heading text-2xl text-primary mt-1 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-secondary animate-bounce" />
                {language === "hi" ? "रेफरल पुरस्कार कार्यक्रम" : "Referral Rewards Program"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {language === "hi" 
                  ? "हमारसाथी पर जुड़ने वाले प्रत्येक मित्र के लिए ₹200 कमाएं — जो आपकी मेम्बरशिप पर रिडीम करने योग्य है! (कम से कम 5 सदस्यों को रेफर करने पर बोनस और 30 दिनों का मुफ्त प्रीमियम अनलॉक होता है)" 
                  : "Earn ₹200 for every friend referred who joins Hamarsathi — redeemable on membership! (Referral bonus active on at least 5 members)"}
              </p>

              <div className="grid md:grid-cols-3 gap-6 mt-6 pt-4 border-t border-border/60">
                {/* Code, Rank, and Balance */}
                <div className="space-y-4 md:col-span-2">
                  <div className="flex flex-wrap gap-4 justify-between items-center">
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{t("dashboard.refCode")}</div>
                      <div className="text-2xl font-bold text-secondary font-mono tracking-widest mt-1 bg-muted px-3 py-1 rounded-lg select-all">
                        {referralStats.referral_code}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{language === "hi" ? "लीडरबोर्ड रैंक" : "Leaderboard Rank"}</div>
                      <div className="text-2xl font-bold text-primary flex items-center gap-1.5 mt-1">
                        <Award className="w-5 h-5 text-secondary" />
                        {language === "hi" ? `रैंक #${referralStats.leaderboard_rank}` : `Rank #${referralStats.leaderboard_rank}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{language === "hi" ? "अर्जित बोनस बैलेंस" : "Bonus Balance"}</div>
                      <div className="text-2xl font-bold text-emerald-600 mt-1">
                        ₹{referralStats.reward_balance || 0}
                        <span className="text-[10px] text-muted-foreground font-normal block">{language === "hi" ? "(मेम्बरशिप पर रिडीम योग्य)" : "(Redeemable on membership)"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Progress towards referrals */}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-muted-foreground font-semibold mb-1">
                      <span>{language === "hi" ? `आमंत्रण प्रगति (मील का पत्थर: ${referralStats.milestone_target || 5} सदस्य)` : `Invite Progress (Milestone: ${referralStats.milestone_target || 5} members)`}</span>
                      <span>{referralStats.referred_count} {language === "hi" ? "जुड़े" : "joined"} ({language === "hi" ? "प्रगति" : "Progress"}: {referralStats.progress || 0}/{referralStats.milestone_target || 5})</span>
                    </div>
                    <div className="h-3 rounded-full bg-muted overflow-hidden flex">
                      <div 
                        className="h-full bg-secondary transition-all" 
                        style={{ width: `${((referralStats.progress || 0) / (referralStats.milestone_target || 5)) * 100}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {language === "hi" 
                        ? `आपके अगले ₹200 डिस्काउंट कूपन कोड और 30 दिनों के मुफ्त प्रीमियम इनाम के लिए ${(referralStats.milestone_target || 5) - (referralStats.progress || 0)} और आमंत्रणों की आवश्यकता है!` 
                        : `${(referralStats.milestone_target || 5) - (referralStats.progress || 0)} more referral(s) until your next ₹200 Coupon Code & 30 days Premium reward!`}
                    </p>
                  </div>

                  {user?.is_mitra && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-900 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-600 shrink-0" />
                        <div>
                          <div className="font-bold text-amber-950">🏆 Official Vivah Mitra ({user.mitra_badge || "District Ambassador"})</div>
                          <div className="text-[11px] text-amber-800">Monthly Recharge Goal: {(referralStats.referred_count || 0) % 5}/5 Valid Referrals for ₹299 Recharge</div>
                        </div>
                      </div>
                      <span className="bg-amber-200 text-amber-950 font-bold px-2 py-0.5 rounded text-[10px] uppercase">Active</span>
                    </div>
                  )}

                  <Button 
                    onClick={handleShareReferral}
                    className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2"
                    data-testid="share-whatsapp-btn"
                  >
                    <Share2 className="w-4 h-4" /> {t("dashboard.shareWhatsapp")}
                  </Button>
                </div>

                {/* QR Code and link */}
                <div className="flex flex-col items-center justify-center border-l md:border-t-0 pt-4 md:pt-0 border-border/60">
                  {qrCodeUrl ? (
                    <div className="bg-white p-2 rounded-xl border border-border shadow-sm">
                      <img src={qrCodeUrl} alt="Referral Link QR Code" className="w-28 h-28" />
                    </div>
                  ) : (
                    <QrCode className="w-24 h-24 text-muted-foreground" />
                  )}
                  <span className="text-[10px] text-muted-foreground mt-2">
                    {language === "hi" ? "पंजीकरण के लिए क्यूआर स्कैन करें" : "Scan QR to Register"}
                  </span>
                </div>
              </div>

              {/* Referred users list inside referrals stats */}
              {referralStats.referred_users?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border/60">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                    {language === "hi" ? "आमंत्रित मित्र जो शामिल हुए:" : "Referred friends who joined:"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {referralStats.referred_users.map((ru, idx) => (
                      <span key={idx} className="text-xs px-2.5 py-1 rounded-full bg-accent/60 text-primary border border-border/40">
                        {ru.name} {ru.profile_complete ? "✔️" : "⏳"}
                      </span>
                    ))}
                  </div>
                </div>
              )}


            </div>
          )}

        </div>

        {/* Right Side: Notifications Widget */}
        <div className="lg:col-span-1">
          <div className="card-soft p-5 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-4 border-b border-border/60 pb-3">
                <h3 className="font-heading text-xl text-primary flex items-center gap-2">
                  <Bell className="w-5 h-5 text-secondary" />
                  {t("dashboard.notifications")}
                </h3>
                {notifications.some(n => !n.read) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleMarkAllRead}
                    className="text-xs text-secondary hover:bg-secondary/10 px-2 py-0.5 h-auto rounded-full"
                    data-testid="mark-all-read-btn"
                  >
                    {language === "hi" ? "सभी साफ करें" : "Clear All"}
                  </Button>
                )}
              </div>

              <div className="space-y-3 overflow-y-auto max-h-[360px] pr-1">
                {notifications.map((n) => (
                  <Link 
                    key={n.notification_id} 
                    to={n.link || "/app"}
                    className={`block p-3 rounded-xl border transition-all ${
                      n.read 
                        ? "bg-background border-border/40 opacity-70" 
                        : "bg-secondary/5 border-secondary/20 hover:bg-secondary/10 shadow-sm"
                    }`}
                    data-testid={`notif-${n.notification_id}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="font-heading text-sm text-primary font-semibold leading-tight">{n.title}</div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-secondary shrink-0 mt-1" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-normal">{n.text}</p>
                    <div className="text-[9px] text-muted-foreground mt-2">
                      {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {new Date(n.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}

                {notifications.length === 0 && (
                  <div className="text-center py-12 text-xs text-muted-foreground italic">
                    {language === "hi" ? "कोई नया अपडेट नहीं है। सब कुछ अद्यतित है!" : "No new notifications. Everything caught up!"}
                  </div>
                )}
              </div>
            </div>

            {/* Quick settings link */}
            <div className="mt-6 pt-4 border-t border-border/60 text-center">
              <Link to="/app/privacy" className="text-xs text-muted-foreground hover:text-secondary flex items-center justify-center gap-1">
                <Shield className="w-3.5 h-3.5" /> {language === "hi" ? "गोपनीयता और दृश्यता सेटिंग्स" : "Privacy & visibility settings"}
              </Link>
            </div>

          </div>
        </div>

      </div>

      {/* Profile Completion Dialog Alert */}
      {showCompleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" data-testid="profile-completion-dialog">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-200 flex flex-col items-center text-center">
            
            {/* Header Icon */}
            <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mb-4 text-amber-600 dark:text-amber-400">
              <Award className="w-8 h-8 animate-bounce" />
            </div>

            {/* Content */}
            <h2 className="font-heading text-2xl text-primary font-bold">
              {language === "hi" ? "अपनी प्रोफ़ाइल पूरी करें!" : "Complete Your Profile!"}
            </h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              {language === "hi"
                ? "सही मैच खोजने का अपना अवसर अनलॉक करें। 5 गुना अधिक दृश्यता और मैचिंग रुचि अनुरोध प्राप्त करने के लिए अपनी प्रोफ़ाइल विवरण (फ़ोटो, शिक्षा, पारिवारिक विवरण) पूरा करें।"
                : "Unlock your chance of finding the perfect match. Complete your profile details (photos, education, family details) to get up to 5x more visibility and matching interest requests."}
            </p>

            {/* Progress indicator */}
            <div className="w-full mt-5 bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
              <div className="bg-secondary h-full transition-all" style={{ width: `${stats?.profile_completion ?? 0}%` }} />
            </div>
            <span className="text-xs text-muted-foreground mt-2 font-medium">
              {language === "hi" 
                ? `आपकी प्रोफ़ाइल ${stats?.profile_completion ?? 0}% पूरी हो चुकी है` 
                : `Your profile is ${stats?.profile_completion ?? 0}% complete`}
            </span>

            {/* Actions */}
            <div className="w-full mt-6 flex flex-col gap-2.5">
              <Button asChild className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground w-full" onClick={handleDismiss}>
                <Link to="/app/profile">
                  {language === "hi" ? "अभी प्रोफ़ाइल पूरी करें" : "Complete Profile Now"} <ArrowRight className="w-4 h-4 ml-1.5"/>
                </Link>
              </Button>
              <Button 
                variant="outline" 
                className="rounded-full w-full" 
                onClick={handleSendEmail} 
                disabled={sendingEmail}
                data-testid="send-checklist-btn"
              >
                {sendingEmail 
                  ? (language === "hi" ? "भेज रहा है..." : "Sending...") 
                  : (language === "hi" ? "ईमेल चेकलिस्ट भेजें" : "Send Email Checklist")}
              </Button>
              <button 
                onClick={handleDismiss} 
                className="text-xs text-muted-foreground hover:text-slate-800 dark:hover:text-slate-200 mt-2 font-medium hover:underline"
                data-testid="dismiss-alert-btn"
              >
                {language === "hi" ? "मुझे बाद में याद दिलाएं" : "Remind Me Later"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── AI Match Compatibility Breakdown Modal ── */}
      {selectedMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-border animate-in zoom-in-95 duration-200 relative">
            <button 
              onClick={() => setSelectedMatch(null)}
              className="absolute top-4 right-4 bg-muted hover:bg-muted/80 text-muted-foreground p-1.5 rounded-full transition"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-6 h-6 text-emerald-500 animate-pulse" />
              <h3 className="font-heading text-2xl text-primary font-bold">
                {language === "hi" ? "अनुकूलता विश्लेषण" : "AI Compatibility Analysis"}
              </h3>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              {language === "hi" 
                ? `आपकी ${selectedMatch.user.name} के साथ समग्र अनुकूलता स्कोर ${selectedMatch.score}% है। यहाँ विस्तृत ब्रेकडाउन है:`
                : `Your overall compatibility score with ${selectedMatch.user.name} is ${selectedMatch.score}%. Here is the detailed breakdown:`}
            </p>

            <div className="space-y-4">
              {/* Religion Score */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground">
                  <span>{language === "hi" ? "धर्म अनुकूलता" : "Religion Matching"}</span>
                  <span className="text-emerald-600 font-bold">{selectedMatch.breakdown.religion_score}/30</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(selectedMatch.breakdown.religion_score / 30) * 100}%` }} />
                </div>
              </div>

              {/* Location Score */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground">
                  <span>{language === "hi" ? "भौगोलिक निकटता" : "Location Proximity"}</span>
                  <span className="text-emerald-600 font-bold">{selectedMatch.breakdown.location_score}/25</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(selectedMatch.breakdown.location_score / 25) * 100}%` }} />
                </div>
              </div>

              {/* Age gap Score */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground">
                  <span>{language === "hi" ? "आयु अंतर" : "Age Gap Matching"}</span>
                  <span className="text-emerald-600 font-bold">{selectedMatch.breakdown.age_score}/20</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(selectedMatch.breakdown.age_score / 20) * 100}%` }} />
                </div>
              </div>

              {/* Caste Score */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground">
                  <span>{language === "hi" ? "जाति अनुकूलता" : "Caste Similarity"}</span>
                  <span className="text-emerald-600 font-bold">{selectedMatch.breakdown.caste_score}/15</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(selectedMatch.breakdown.caste_score / 15) * 100}%` }} />
                </div>
              </div>

              {/* Education Score */}
              <div>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground">
                  <span>{language === "hi" ? "शैक्षणिक पृष्ठभूमि" : "Education & Career Match"}</span>
                  <span className="text-emerald-600 font-bold">{selectedMatch.breakdown.education_score}/10</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(selectedMatch.breakdown.education_score / 10) * 100}%` }} />
                </div>
              </div>
            </div>

            {/* AI Summary inside Modal */}
            {selectedMatch.reason && (
              <div className="mt-6 bg-emerald-50/50 dark:bg-emerald-950/10 border border-emerald-200/50 dark:border-emerald-900/30 rounded-2xl p-4 text-xs text-foreground leading-relaxed">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-4 h-4 animate-pulse" /> {language === "hi" ? "एआई का विस्तृत सारांश:" : "Detailed AI Summary:"}
                </span>
                {selectedMatch.reason}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button 
                onClick={() => setSelectedMatch(null)}
                className="rounded-full px-6 bg-primary hover:bg-primary/95 text-white"
              >
                {language === "hi" ? "ठीक है" : "Close"}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function Stat({ icon: Icon, label, value, to, tileId }) {
  const inner = (
    <div className="card-soft p-5 h-full hover:shadow-md transition-shadow cursor-pointer" data-testid={tileId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-secondary" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
          <div className="font-heading text-2xl text-foreground mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export function ProfileTile({ p }) {
  const { language } = useLanguage();
  const [interestSent, setInterestSent] = useState(p.interest_status === "sent" || false);
  const [shortlisted, setShortlisted] = useState(p.is_shortlisted || false);
  const [ignored, setIgnored] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInterest = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (interestSent) return;
    setLoading(true);
    try {
      await api.post(`/interests/send/${p.user_id}`);
      setInterestSent(true);
      toast.success("Interest sent successfully!");
    } catch (err) {
      toast.error("Failed to send interest.");
    } finally {
      setLoading(false);
    }
  };

  const handleShortlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      const { data } = await api.post(`/shortlist/${p.user_id}`);
      setShortlisted(data.op === "added");
      toast.success(data.op === "added" ? "Added to shortlist" : "Removed from shortlist");
    } catch (err) {
      toast.error("Failed to update shortlist.");
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/privacy/block/${p.user_id}`);
      setIgnored(true);
      toast.success("Profile hidden/ignored.");
    } catch (err) {
      toast.error("Failed to ignore profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPhoto = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      await api.post(`/profile/${p.user_id}/request-photo`);
      toast.success(language === "hi" ? `फ़ोटो अनुरोध ${p.name} को भेज दिया गया है!` : `Photo request sent to ${p.name}!`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (ignored) return null;

  const formatHeight = (cm) => {
    if (!cm) return "";
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}ft ${inches}in`;
  };

  const hasPhoto = p.photos && p.photos.length > 0;
  const mainPhoto = hasPhoto ? photoUrl(p.photos[0]) : null;
  const photoCount = p.photos ? p.photos.length : 0;

  const getActiveStatus = () => {
    const lastChar = p.user_id ? p.user_id.slice(-1) : "";
    if (["0", "1", "3", "5", "7"].includes(lastChar)) {
      return language === "hi" ? "आज सक्रिय" : "Active Today";
    } else if (["2", "4", "6"].includes(lastChar)) {
      return language === "hi" ? "कल सक्रिय" : "Active Yesterday";
    } else {
      return language === "hi" ? "हाल ही में सक्रिय" : "Active recently";
    }
  };

  const activeStatus = getActiveStatus();
  const heightStr = formatHeight(p.height_cm);
  
  const line1Parts = [heightStr, p.city || p.district, p.caste].filter(Boolean);
  const line1 = line1Parts.join("  •  ") || "Jharkhand";
  
  const line2Parts = [p.occupation || "Profession", p.income].filter(Boolean);
  const line2 = line2Parts.join("  •  ");
  
  const line3 = p.education || "Graduate";

  return (
    <div className="bg-card rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-border/60 hover:shadow-lg transition-all duration-300 relative flex flex-col sm:flex-row sm:h-[225px] group" data-testid={`profile-tile-${p.user_id}`}>
      
      {/* Profile Image Section */}
      <div className="relative w-full h-64 sm:h-full sm:w-44 md:w-48 lg:w-52 shrink-0 bg-muted overflow-hidden">
        {hasPhoto ? (
          <img
            src={mainPhoto}
            alt={p.name}
            className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/95 via-primary/80 to-primary/45 flex flex-col items-center justify-center p-4 min-h-[180px]">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-2">
              <Users className="w-6 h-6 text-white/60" />
            </div>
            {!hasPhoto && (
              <button
                type="button"
                onClick={handleRequestPhoto}
                disabled={loading}
                className="px-3 py-1.5 rounded-full border border-white/60 bg-white/10 backdrop-blur-sm text-white font-semibold text-[10px] hover:bg-white hover:text-black transition-all disabled:opacity-50"
              >
                📷 {language === "hi" ? "फ़ोटो मांगें" : "Request Photo"}
              </button>
            )}
          </div>
        )}

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
          {p.is_premium && (
            <span className="bg-gradient-to-r from-red-600 to-pink-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm">
              Pro Max
            </span>
          )}
          <span className="bg-black/40 backdrop-blur-sm text-[9px] text-white px-2 py-0.5 rounded-full font-medium">
            {activeStatus}
          </span>
        </div>

        {/* Verification Checkmark */}
        {p.is_verified && (
          <div className="absolute top-3 right-3 bg-secondary p-1 rounded-full border border-white shadow-md z-10" title="Verified Member">
            <Check className="w-3 h-3 text-secondary-foreground" strokeWidth={3.5} />
          </div>
        )}
      </div>

      {/* Profile Details Section */}
      <div className="p-3.5 sm:p-4 flex flex-col justify-between flex-grow min-w-0 sm:h-full gap-2">
        <div>
          {/* Header Name & Match Score */}
          <div className="flex justify-between items-start gap-2 mb-1">
            <h3 className="font-heading text-lg font-bold text-primary truncate hover:underline">
              <Link to={`/app/profile/${p.user_id}`} onClick={(e) => e.stopPropagation()}>
                {(p.name || "Unknown").split(" ")[0]}, <span className="font-normal text-muted-foreground">{p.age}</span>
              </Link>
            </h3>
            {/* Simple Match Score simulation if not provided */}
            <div className="bg-primary/5 text-primary text-[10px] font-bold px-2 py-0.5 rounded-md border border-primary/10 shrink-0">
              {p.match_percentage || 95}% Match
            </div>
          </div>

          {/* Quick Demographics Line */}
          <p className="text-xs font-semibold text-muted-foreground mb-2 truncate">
            {p.religion} • {p.caste || "Any Caste"}
          </p>

          {/* Structured Key Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-foreground/80 bg-muted/30 p-2.5 rounded-xl border border-border/40">
            <div className="flex items-center gap-1.5 truncate">
              <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                📏
              </span>
              <span className="truncate">{heightStr || (language === "hi" ? "कद उपलब्ध नहीं" : "Height N/A")}</span>
            </div>
            <div className="flex items-center gap-1.5 truncate">
              <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                📍
              </span>
              <span className="truncate">{[p.block, p.district || p.city].filter(Boolean).join(", ") || "Jharkhand"}</span>
            </div>
            <div className="flex items-center gap-1.5 truncate">
              <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                💼
              </span>
              <span className="truncate">{p.occupation || (language === "hi" ? "व्यवसाय उपलब्ध नहीं" : "Occupation N/A")}</span>
            </div>
            <div className="flex items-center gap-1.5 truncate">
              <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                🎓
              </span>
              <span className="truncate">{p.education || "Graduate"}</span>
            </div>
            {p.income && (
              <div className="flex items-center gap-1.5 truncate">
                <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                  💰
                </span>
                <span className="truncate">{p.income}</span>
              </div>
            )}
            {p.marital_status && (
              <div className="flex items-center gap-1.5 truncate">
                <span className="w-3.5 h-3.5 rounded-full bg-background flex items-center justify-center text-[9px] text-muted-foreground shrink-0 border border-border/50">
                  💍
                </span>
                <span className="truncate">{p.marital_status}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions Row */}
        <div className="flex gap-2 items-center pt-2 border-t border-border/60">
          {/* Ignore (X) icon button */}
          <button
            onClick={handleIgnore}
            disabled={loading}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors shrink-0"
            title="Ignore Profile"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          {/* Shortlist (Ghost) button */}
          <Button
            variant="outline"
            onClick={handleShortlist}
            disabled={loading}
            className={`flex-1 rounded-full h-8 text-xs font-semibold ${
              shortlisted 
                ? "border-amber-500 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20" 
                : "border-primary text-primary hover:bg-primary/5"
            }`}
          >
            <Star className="w-3 h-3 mr-1" fill={shortlisted ? "currentColor" : "none"} />
            {shortlisted ? (language === "hi" ? "शॉर्टलिस्टेड" : "Shortlisted") : (language === "hi" ? "शॉर्टलिस्ट" : "Shortlist")}
          </Button>

          {/* Connect Now / Interest (Primary filled) button */}
          <Button
            onClick={handleInterest}
            disabled={interestSent || loading}
            className={`flex-1 rounded-full h-8 text-xs font-bold ${
              interestSent
                ? "bg-emerald-600/10 border border-emerald-600/20 text-emerald-700 hover:bg-emerald-600/10 cursor-default"
                : "bg-primary text-primary-foreground hover:bg-primary/95"
            }`}
          >
            {interestSent ? (
              <>✓ {language === "hi" ? "भेजा गया" : "Sent"}</>
            ) : (
              <>💖 {language === "hi" ? "कनेक्ट" : "Connect"}</>
            )}
          </Button>
        </div>
      </div>

      {/* Main card link wrapper overlay for detail page view (only catches clicks outside action buttons) */}
      <Link 
        to={`/app/profile/${p.user_id}`} 
        className="absolute inset-0 z-0 pointer-events-none" 
      />
    </div>
  );
}

export function AIRecommendedProfileTile({ rec, onShowBreakdown }) {
  const { user: p, score, reason } = rec;
  const { language } = useLanguage();
  const [interestSent, setInterestSent] = useState(p.interest_status === "sent" || false);
  const [loading, setLoading] = useState(false);

  const handleInterest = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (interestSent) return;
    setLoading(true);
    try {
      await api.post(`/interests/send/${p.user_id}`);
      setInterestSent(true);
      toast.success("Interest sent successfully!");
    } catch {
      toast.error("Failed to send interest.");
    } finally {
      setLoading(false);
    }
  };

  const computeAge = (dob) => {
    if (!dob) return 25;
    const dobDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatHeight = (cm) => {
    if (!cm) return "";
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}ft ${inches}in`;
  };

  const hasPhoto = p.photos && p.photos.length > 0;
  const mainPhoto = hasPhoto ? photoUrl(p.photos[0]) : null;
  const heightStr = formatHeight(p.height_cm);
  
  const line1Parts = [heightStr, p.city || p.district, p.caste].filter(Boolean);
  const line1 = line1Parts.join(" • ") || "Jharkhand";
  
  const line2Parts = [p.occupation || "Profession", p.income].filter(Boolean);
  const line2 = line2Parts.join(" • ");
  
  const line3 = p.education || "Graduate";

  return (
    <div className="w-[280px] shrink-0 rounded-3xl border border-border/80 bg-white dark:bg-zinc-900 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col snap-start relative">
      {/* Top Photo Section */}
      <div className="h-[180px] w-full relative bg-muted overflow-hidden">
        {hasPhoto ? (
          <img
            src={mainPhoto}
            alt={p.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.02]"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-800 to-teal-900 flex items-center justify-center">
            <Users className="w-8 h-8 text-white/40" />
          </div>
        )}

        {/* Match Percentage Badge */}
        <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-amber-200 animate-pulse" />
          <span>{score}% {language === "hi" ? "मैच" : "Match"}</span>
        </div>

        {/* Verification indicator */}
        <div className="absolute bottom-2 left-3 z-10 bg-black/40 backdrop-blur-md text-[9px] text-white font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
          <BadgeCheck className="w-3.5 h-3.5 text-emerald-400 fill-emerald-400/20" />
          {p.is_verified ? (language === "hi" ? "सत्यापित" : "Verified") : (language === "hi" ? "सक्रिय" : "Active")}
        </div>
      </div>

      {/* Bottom Content Section */}
      <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
        <div>
          {/* Name & Age */}
          <h3 className="font-heading text-lg font-bold text-foreground leading-tight truncate">
            {p.name.split(" ")[0]}, <span className="font-normal text-muted-foreground">{computeAge(p.dob)}</span>
          </h3>

          {/* Quick Info Lines */}
          <div className="text-[11px] text-muted-foreground mt-1.5 space-y-0.5 leading-snug">
            <p className="truncate font-medium">{line1}</p>
            <p className="truncate">{line2}</p>
            <p className="truncate">{line3}</p>
          </div>

          {/* AI Analysis Box */}
          {reason && (
            <div className="mt-2.5 bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-2 text-[10px] text-foreground leading-normal">
              <span className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mb-0.5">
                <Sparkles className="w-3 h-3" /> {language === "hi" ? "एआई विश्लेषण:" : "AI Insight:"}
              </span>
              <p className="line-clamp-2 text-muted-foreground">{reason}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-1 z-10">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onShowBreakdown(rec);
            }}
            className="rounded-full h-8 text-[10px] border-border text-foreground hover:bg-muted font-semibold"
          >
            📊 {language === "hi" ? "स्कोर" : "Analysis"}
          </Button>

          <Button
            type="button"
            onClick={handleInterest}
            disabled={interestSent || loading}
            className={`rounded-full h-8 text-[10px] font-bold ${
              interestSent
                ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0"
            }`}
          >
            {interestSent ? (language === "hi" ? "✓ भेजा" : "✓ Sent") : (language === "hi" ? "💖 कनेक्ट" : "💖 Connect")}
          </Button>
        </div>
      </div>

      {/* Main card link wrapper overlay for detail page view */}
      <Link 
        to={`/app/profile/${p.user_id}`} 
        className="absolute inset-0 z-0" 
      />
    </div>
  );
}

